import React from "react";
import { Alert, AlertTitle } from "@mui/material";
import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";

export enum WidgetErrorType {
  NO_DATA = "no_data",
  INVALID_DATASET = "invalid_dataset",
}

// 'WidgetAlert' displays an alert message within a widget.
export interface WidgetAlertProps {
  // @param {("error"|"warning"|"info"|"success")} [props.severity] - Alert type. Default is "success" 
  severity?: "error" | "warning" | "info" | "success";
  // @param {object} [props.style] - Custom styles for the alert. Default is { margin: "1rem" }
  style?: object;
  // @param {string} props.alertTitleText - The title text for the alert - required.
  alertTitleText?: string;
  // @param {ReactNode} [props.children] - The children to render inside the alert - optional.
  children?: ReactNode;

  type?: WidgetErrorType;
}

export default function WidgetAlert(props: WidgetAlertProps): JSX.Element {
  const { i18n, t } = useTranslation();
  const defaultStyle = { margin: "1rem" };

  const commonErrorConfigs = useMemo<Record<WidgetErrorType, WidgetAlertProps>>(() => {
    return {
      [WidgetErrorType.NO_DATA]: {
        alertTitleText: t("api-request.no-content-message"),
        severity: "warning"
      },
      [WidgetErrorType.INVALID_DATASET]: {
        alertTitleText: t("api-request.invalid-dataset-message"),
        severity: "warning"
      }
    };
  }, []);
  const errorConfig = props.type && commonErrorConfigs[props.type];
  const severity = props.severity || errorConfig?.severity;
  const style = props.style || errorConfig?.style || defaultStyle;
  const title = props.alertTitleText && t(props.alertTitleText) || errorConfig?.alertTitleText;
  const children = props.children || errorConfig?.children;

  return (
    <Alert severity={severity} sx={style}>
      <AlertTitle>{title}</AlertTitle>
        {children}
    </Alert>
  )
}